import { useMemo } from 'react';
import { Box, Divider, Paper, Stack, Table, TableBody, TableContainer, TableHead, Typography } from '@mui/material';
import { BorderWrapper, Cell, HeaderText, RowText, StyledTableHead, StyledTableRow } from '@styles/CommonStyles';
import { formatCurrency } from '@shared/commonFunctions';
import { EBureauType } from '../../interfaces';
import { ITradeLines, ITradeLine } from './interfaces/interfaces';

 export const TRADE_LINE_HEADERS = [
   'Firm Name/ID\nAccount Number\nKOB/Trade Type\nStatus\nPhone#',
   'Opened\nReported\nAcct. Type',
   'Credit Limit\nHigh Credit\nChargeoff\nLst. Payment',
   'Balance\nPast Due\nOrin. Amt.\nClose Ind',
   'MoPymnt\nMoRep\nDate Closed\nOwner',
   '30 / 60 / 90\nPattern\n \nTerms',
 ];

const renderTradeLines = (paymentPattern: string) => {
  if (!paymentPattern?.length) return null;

  const patternLines: string[] = [];
  let twelveCharPaymentPattern = '';

  for (let i = 0; i < paymentPattern.length; i++) {
    const char = paymentPattern[i];

    if (char === '/' || char.trim() === '') continue;

    twelveCharPaymentPattern += char;

    if (twelveCharPaymentPattern.length === 12) {
      patternLines.push(twelveCharPaymentPattern.trim());
      twelveCharPaymentPattern = '';
    }
  }

  if (twelveCharPaymentPattern.trim()) {
    patternLines.push(twelveCharPaymentPattern.trim());
  }

  return (
    <>
      {patternLines.map((pattern, index) => (
        <>
          <RowText>{pattern}</RowText>
          {index < patternLines.length - 1 && <Divider />}
        </>
      ))}
    </>
  );
};

const renderTradeLinesHeader = () => (
  <TableHead>
    <StyledTableHead>
      <Cell>
        <Stack>
          <HeaderText>Firm Name/ID</HeaderText>
          <HeaderText>Account Number</HeaderText>
          <HeaderText>KOB/Trade Type</HeaderText>
          <HeaderText>Status</HeaderText>
          <HeaderText>Phone#</HeaderText>
        </Stack>
      </Cell>
      <Cell>
        <Stack>
          <HeaderText>Opened</HeaderText>
          <HeaderText>Reported</HeaderText>
          <HeaderText>Acct. Type</HeaderText>
        </Stack>
      </Cell>
      <Cell>
        <Stack>
          <HeaderText>Credit Limit</HeaderText>
          <HeaderText>High Credit</HeaderText>
          <HeaderText>Chargeoff</HeaderText>
          <HeaderText>Lst. Payment</HeaderText>
        </Stack>
      </Cell>
      <Cell>
        <Stack>
          <HeaderText>Balance</HeaderText>
          <HeaderText>Past Due</HeaderText>
          <HeaderText>Orin. Amt.</HeaderText>
          <HeaderText>Close Ind</HeaderText>
        </Stack>
      </Cell>
      <Cell>
        <Stack>
          <HeaderText>MoPymnt</HeaderText>
          <HeaderText>MoRep</HeaderText>
          <HeaderText>Date Closed</HeaderText>
          <HeaderText>Owner</HeaderText>
        </Stack>
      </Cell>
      <Cell>
        <Stack>
          <Stack
            direction="row"
            gap={1}
          >
            <HeaderText>30</HeaderText>
            <HeaderText>60</HeaderText>
            <HeaderText>90</HeaderText>
          </Stack>
          <HeaderText>Pattern</HeaderText>
          <Typography>&nbsp;</Typography>
          <HeaderText>Terms</HeaderText>
        </Stack>
      </Cell>
    </StyledTableHead>
  </TableHead>
);

const renderTradeLinesRows = ({ sortedTradeLines }: { sortedTradeLines: ITradeLine[] }) => (
  <TableBody>
    {sortedTradeLines?.map((data, index) => (
      <StyledTableRow
        key={data.firmID}
        index={index}
      >
        <Cell>
          <Stack
            direction="column"
            gap={1}
          >
            <RowText fontWeight="bold !important">{`${data.firmName ? `${data.firmName}/` : ''}  ${data.firmID ?? ''}`}</RowText>
            <RowText>{data.accountNumber ?? ''}</RowText>
            <RowText>{`${data.kob ? `${data.kob}/` : ''}  ${data.tradeType ?? ''}`}</RowText>
            <RowText>{data.status ?? ''}</RowText>
            <RowText>{data.phone ?? ''}</RowText>
            <RowText>{data.narativeDescription?.toUpperCase() ?? ''}</RowText>
          </Stack>
        </Cell>
        <Cell>
          <Stack
            direction="column"
            minWidth="75px"
            gap={1}
          >
            <RowText>{data.dateOpened ?? ''}</RowText>
            <RowText>{data.dateReported ?? ''}</RowText>
            <RowText>{data.accountType ?? ''}</RowText>
          </Stack>
        </Cell>
        <Cell>
          <Stack
            direction="column"
            minWidth="60px"
            gap={1}
          >
            <RowText>{data.creditLimit ? formatCurrency(data.creditLimit) : ''}</RowText>
            <RowText>{data.highCredit ? formatCurrency(data.highCredit) : ''}</RowText>
            <RowText>{data.chargeOff ? formatCurrency(data.chargeOff) : ''}</RowText>
            <RowText>{data.dateLastPayment ?? data?.dateLastActivity ?? ''}</RowText>
          </Stack>
        </Cell>
        <Cell>
          <Stack
            direction="column"
            minWidth="60px"
            gap={1}
          >
            <RowText>{data.balance ? formatCurrency(data.balance) : ''}</RowText>
            <RowText>{data.pastDue ? formatCurrency(data.pastDue) : ''}</RowText>
            <RowText>{data.originalLoanValue ? formatCurrency(data.originalLoanValue) : ''}</RowText>
            <RowText>{data.closedIndicator ?? ''}</RowText>
          </Stack>
        </Cell>
        <Cell>
          <Stack
            direction="column"
            minWidth="60px"
            gap={1}
          >
            <RowText>{data.monthlyPayment ? formatCurrency(data.monthlyPayment) : ''}</RowText>
            <RowText> {data?.monthlyRepayment ?? ''}</RowText>
            <RowText>{data.dateClosed ?? ''}</RowText>
            <RowText> {data.owner ?? ''} </RowText>
          </Stack>
        </Cell>
        <Cell>
          <Stack
            flexWrap="wrap"
            direction="column"
            minWidth="95px"
            gap={1}
          >
            <Stack
              direction="row"
              gap={1}
            >
              <RowText>{data.past30Days}</RowText>
              <RowText>{data.past60Days}</RowText>
              <RowText>{data.past90Days}</RowText>
            </Stack>
            <Stack>{renderTradeLines(data.paymentPattern ?? '')}</Stack>

            <RowText>&nbsp;</RowText>
            <RowText>{data.terms ?? ''}</RowText>
          </Stack>
        </Cell>
      </StyledTableRow>
    ))}
  </TableBody>
);

export function sortTradelinesByClosedIndicatorStatus<T extends { closedIndicator: string }>(tradeLines: T[], sortOrder: string[]): T[] {
  return tradeLines.sort((a, b) => {
    // Convert to lowercase for case-insensitive comparison
    const aClosedIndicator = a.closedIndicator.toLowerCase();
    const bClosedIndicator = b.closedIndicator.toLowerCase();
    const caseInsensitiveSortOrder = sortOrder.map((status) => status.toLowerCase());

    const aIndex = caseInsensitiveSortOrder.indexOf(aClosedIndicator);
    const bIndex = caseInsensitiveSortOrder.indexOf(bClosedIndicator);

    // If closedIndicator not found in sort order put it at the end
    const aValue = aIndex === -1 ? sortOrder.length : aIndex;
    const bValue = bIndex === -1 ? sortOrder.length : bIndex;

    return aValue - bValue;
  });
}

export function chunkPaymentPattern(paymentPattern: string): string[] {
  if (!paymentPattern?.length) return [];

  const patternLines: string[] = [];
  let twelveCharPaymentPattern = '';

  for (let i = 0; i < paymentPattern.length; i++) {
    const char = paymentPattern[i];
    if (char === '/' || char.trim() === '') continue;

    twelveCharPaymentPattern += char;

    if (twelveCharPaymentPattern.length === 12) {
      patternLines.push(twelveCharPaymentPattern.trim());
      twelveCharPaymentPattern = '';
    }
  }

  if (twelveCharPaymentPattern.trim()) {
    patternLines.push(twelveCharPaymentPattern.trim());
  }

  return patternLines;
}


export const TradeLines = ({ tradeLines, bureauType }: ITradeLines) => {
  const sortedTradeLines =
    bureauType === EBureauType.Transunion
      ? useMemo(() => {
          if (!tradeLines?.length) return [];
          return sortTradelinesByClosedIndicatorStatus([...tradeLines], ['Open', 'Closed']);
        }, [tradeLines, bureauType])
      : tradeLines;

  return (
    <Box marginBottom={2}>
      <Typography
        fontWeight={600}
        variant="h3"
      >
        Trade Lines
      </Typography>
      <BorderWrapper>
        <TableContainer
          component={Paper}
          sx={{ overflow: 'visible' }}
        >
          <Table>
            {renderTradeLinesHeader()}
            {renderTradeLinesRows({ sortedTradeLines })}
          </Table>
        </TableContainer>
      </BorderWrapper>
    </Box>
  );
};
